import { useState } from 'react';
import { useGameStore } from '@/store/gameStore';
import { Settings, SettingsButton } from '@/components/Settings';

export function MainMenu() {
  const navigateTo = useGameStore((s) => s.navigateTo);
  const loadDailyChallenge = useGameStore((s) => s.loadDailyChallenge);
  const enterPractice = useGameStore((s) => s.enterPractice);
  const currentStreak = useGameStore((s) => s.progress.daily.currentStreak);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const menuItems = [
    { label: 'Journey', enabled: true, key: 'journey' },
    { label: 'Daily Challenge', enabled: true, key: 'daily' },
    { label: 'Practice', enabled: true, key: 'practice' },
    { label: 'Collection', enabled: false, key: 'collection' },
  ] as const;

  function handleItemClick(key: string, enabled: boolean) {
    if (!enabled) return;
    if (key === 'journey') {
      navigateTo('puzzleSelect');
    } else if (key === 'daily') {
      loadDailyChallenge();
    } else if (key === 'practice') {
      enterPractice();
    }
  }

  function renderLabel(item: typeof menuItems[number]): React.ReactNode {
    if (item.key === 'daily' && currentStreak > 0) {
      return (
        <>
          {item.label} {'\uD83D\uDD25'} {currentStreak}
        </>
      );
    }
    return item.label;
  }

  return (
    <div style={containerStyle}>
      {/* Animated cosmic background */}
      <div style={cosmicBgStyle} />

      {/* Background decorative glow elements */}
      <div style={bgGlow1} />
      <div style={bgGlow2} />
      <div style={bgGlow3} />

      <div style={contentStyle}>
        {/* Title */}
        <div style={titleContainerStyle}>
          <h1 style={titleStyle}>RESONANCE</h1>
          <p style={subtitleStyle}>Harmonize the frequencies</p>
        </div>

        {/* Menu items */}
        <nav style={navStyle} role="navigation" aria-label="Main menu">
          {menuItems.map((item) => {
            const isHovered = hoveredItem === item.key;
            const baseStyle = item.enabled
              ? { ...menuButtonStyle, ...(isHovered ? menuButtonHoverStyle : {}) }
              : { ...menuButtonStyle, ...menuButtonDisabledStyle };

            return (
              <button
                key={item.key}
                style={baseStyle}
                onMouseEnter={() => setHoveredItem(item.key)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => handleItemClick(item.key, item.enabled)}
                disabled={!item.enabled}
                aria-label={!item.enabled ? `${item.label} (coming soon)` : item.label}
              >
                <span style={menuLabelStyle}>{renderLabel(item)}</span>
                {!item.enabled && <span style={comingSoonStyle}>Coming Soon</span>}
              </button>
            );
          })}
        </nav>

        {/* Version */}
        <p style={versionStyle}>v0.2.0</p>
      </div>

      {/* Settings button -- bottom right */}
      <SettingsButton style={{ position: 'absolute', bottom: '24px', right: '24px' }} />

      {/* Settings overlay */}
      <Settings />
    </div>
  );
}

// ── Styles ──

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#050510',
  position: 'relative',
  overflow: 'hidden',
};

const cosmicBgStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'radial-gradient(ellipse at 30% 20%, rgba(114, 9, 183, 0.08) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(58, 12, 163, 0.06) 0%, transparent 50%), radial-gradient(circle at 50% 50%, #0d0d2b 0%, #050510 100%)',
  backgroundSize: '200% 200%',
  animation: 'cosmicDrift 20s ease-in-out infinite',
  pointerEvents: 'none',
};

const bgGlow1: React.CSSProperties = {
  position: 'absolute',
  width: '600px',
  height: '600px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(114, 9, 183, 0.12) 0%, transparent 70%)',
  top: '-200px',
  right: '-100px',
  pointerEvents: 'none',
};

const bgGlow2: React.CSSProperties = {
  position: 'absolute',
  width: '500px',
  height: '500px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(58, 12, 163, 0.1) 0%, transparent 70%)',
  bottom: '-150px',
  left: '-100px',
  pointerEvents: 'none',
};

const bgGlow3: React.CSSProperties = {
  position: 'absolute',
  width: '400px',
  height: '400px',
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(199, 125, 255, 0.05) 0%, transparent 60%)',
  top: '40%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  pointerEvents: 'none',
};

const contentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '48px',
  zIndex: 1,
  padding: '24px',
  width: '100%',
  maxWidth: '480px',
};

const titleContainerStyle: React.CSSProperties = {
  textAlign: 'center',
  marginBottom: '16px',
};

const titleStyle: React.CSSProperties = {
  fontSize: 'clamp(36px, 8vw, 64px)',
  fontWeight: 300,
  letterSpacing: '0.3em',
  color: '#e8e8e4',
  fontFamily: "'Cormorant Garamond', 'Palatino', 'Georgia', serif",
  textShadow: '0 0 20px rgba(199, 125, 255, 0.6), 0 0 40px rgba(114, 9, 183, 0.4), 0 0 80px rgba(114, 9, 183, 0.2), 0 0 120px rgba(58, 12, 163, 0.1)',
  margin: 0,
  background: 'linear-gradient(135deg, #f8f9fa 0%, #c77dff 40%, #b197fc 60%, #f8f9fa 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  animation: 'titlePulse 4s ease-in-out infinite',
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 'clamp(13px, 2vw, 16px)',
  color: 'rgba(232, 232, 228, 0.35)',
  fontWeight: 300,
  letterSpacing: '0.2em',
  marginTop: '12px',
  fontFamily: "'Cormorant Garamond', 'Palatino', 'Georgia', serif",
  textTransform: 'lowercase',
  fontStyle: 'italic',
};

const navStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  width: '100%',
  maxWidth: '320px',
};

const menuButtonStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '16px 32px',
  background: 'rgba(255, 255, 255, 0.04)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '12px',
  color: '#e8e8e4',
  fontSize: '16px',
  fontWeight: 400,
  letterSpacing: '0.08em',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  outline: 'none',
  position: 'relative',
};

const menuButtonHoverStyle: React.CSSProperties = {
  background: 'rgba(114, 9, 183, 0.15)',
  borderColor: 'rgba(199, 125, 255, 0.35)',
  boxShadow: '0 0 20px rgba(114, 9, 183, 0.2), 0 0 40px rgba(114, 9, 183, 0.08), inset 0 0 20px rgba(114, 9, 183, 0.05)',
  transform: 'translateY(-1px)',
};

const menuButtonDisabledStyle: React.CSSProperties = {
  opacity: 0.3,
  cursor: 'not-allowed',
};

const menuLabelStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
};

const comingSoonStyle: React.CSSProperties = {
  fontSize: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  color: 'rgba(232, 232, 228, 0.25)',
  position: 'absolute',
  right: '16px',
};

const versionStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'rgba(232, 232, 228, 0.15)',
  letterSpacing: '0.05em',
  marginTop: '24px',
};
